import { useTranslation } from "react-i18next";
import NewModal from "../../shared/Modals/NewModal";
import { faLinkedin, faInstagram, faYoutube, faTiktok } from "@fortawesome/free-brands-svg-icons";
import {
  faArrowLeft, faWandMagicSparkles, faCheck, faSave,
  faEye, faBullseye, faHeart, faLayerGroup, faComments, faRotate,
  faAlignLeft, faImage, faVideo, faBolt, faChartBar, faGlobe,
  faLightbulb, faBookOpen, faMicrophone, faTag, faUsers, faStar,
  faBullhorn, faGraduationCap, faFaceSmile, faBriefcase,
  faFire, faHandshake, faPenNib, faHourglassHalf,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import { useFormik } from "formik";
import PostAssistantForm from "@serie3/mymediaplanner/Modules/Social/Forms/PostAssistantForm";
import TextArea from "../../Form/TextArea";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import listHandler from "@serie3/common/Domain/listHandler";
import YoutubeIcon from "../../shared/YoutubeIcon";

const PILLAR_COLORS = ["#038edc", "#51d28c", "#f1734f", "#564ab1", "#f7cc53", "#5fd0f3"];

const STEPS = [
  { label: "Platform" },
  { label: "Goal" },
  { label: "Pillar" },
  { label: "Content Type" },
  { label: "Tone" },
  { label: "Format" },
  { label: "Topic" },
];

const PLATFORMS = [
  { key: "linkedin",   icon: faLinkedin,  color: "#0077b5", label: "LinkedIn" },
  { key: "instagram",  icon: faInstagram, color: "#e1306c", label: "Instagram" },
  { key: "youtube",    icon: faYoutube,   color: "#ff0000", label: "YouTube" },
  { key: "tiktok",     icon: faTiktok,    color: "#010101", label: "TikTok" },
  { key: "other",      icon: faGlobe,     color: "#9299a8", label: "Other" },
];

const CONTENT_TYPES = [
  { key: "Educational",   icon: faLightbulb,  color: "#038edc", label: "Educational",   description: "Teach something useful." },
  { key: "Story",         icon: faBookOpen,   color: "#f1734f", label: "Story",          description: "Share an experience or journey." },
  { key: "Opinion",       icon: faMicrophone, color: "#564ab1", label: "Opinion",        description: "Share your take on a topic." },
  { key: "Promotional",   icon: faTag,        color: "#f0a500", label: "Promotional",    description: "Promote a product, service or offer." },
  { key: "Interactive",   icon: faUsers,      color: "#51d28c", label: "Interactive",    description: "Get your audience to respond." },
];

const FORMATS = [
  { key: "Text",       icon: faAlignLeft, color: "#038edc", label: "Text" },
  { key: "Image",      icon: faImage,     color: "#20a56e", label: "Image" },
  { key: "Carousel",   icon: faLayerGroup,color: "#564ab1", label: "Carousel" },
  { key: "ShortVideo", icon: faBolt,      color: "#f0a500", label: "Short Video" },
  { key: "Video",      icon: faVideo,     color: "#f1734f", label: "Video" },
  { key: "Poll",       icon: faChartBar,  color: "#51d28c", label: "Poll" },
];

const GOALS = [
  {
    key: "reach",
    icon: faEye,
    color: "#038edc",
    tag: "Grow",
    label: "Get seen",
    description: "Choose this if you want new people to discover you.",
    example: "Viral hooks, broad topics, simple tips.",
    recommended: 40,
  },
  {
    key: "engagement",
    icon: faComments,
    color: "#20a56e",
    tag: "Interact",
    label: "Get reactions",
    description: "Choose this if you want likes, comments or shares.",
    example: "Opinions, questions, hot takes.",
    recommended: 15,
  },
  {
    key: "brand",
    icon: faHeart,
    color: "#564ab1",
    tag: "Trust",
    label: "Build trust",
    description: "Choose this if you want people to see you as an expert.",
    example: "Insights, experience, stories.",
    recommended: 30,
  },
  {
    key: "retention",
    icon: faRotate,
    color: "#f0a500",
    tag: "Return",
    label: "Keep your audience",
    description: "Choose this if you want followers to come back.",
    example: "Series, updates, follow-ups.",
    recommended: 5,
  },
  {
    key: "conversion",
    icon: faBullseye,
    color: "#f1734f",
    tag: "Buy",
    label: "Get customers",
    description: "Choose this if you want clicks, signups or sales.",
    example: "Offers, CTAs, product posts.",
    recommended: 10,
  },
];

// Maps translation tone keys → icon + color (EN + DE). Fallback used for unknown keys.
const TONE_META = {
  // English
  Informative:    { icon: faLightbulb,      color: "#038edc" },
  Entertaining:   { icon: faFaceSmile,      color: "#f0a500" },
  Persuasive:     { icon: faBullhorn,       color: "#f1734f" },
  Instructional:  { icon: faGraduationCap,  color: "#564ab1" },
  Professional:   { icon: faBriefcase,      color: "#374151" },
  Inspirational:  { icon: faFire,           color: "#f7cc53" },
  Casual:         { icon: faHandshake,      color: "#51d28c" },
  Humorous:       { icon: faFaceSmile,      color: "#f0a500" },
  Empathetic:     { icon: faHeart,          color: "#e91e8c" },
  Storytelling:   { icon: faBookOpen,       color: "#20a56e" },
  Authoritative:  { icon: faPenNib,         color: "#374151" },
  Urgent:         { icon: faHourglassHalf,  color: "#f1734f" },
  // German
  "Informativ":   { icon: faLightbulb,      color: "#038edc" },
  "Unterhaltsam": { icon: faFaceSmile,      color: "#f0a500" },
  "Überzeugend":  { icon: faBullhorn,       color: "#f1734f" },
  "Anleitend":    { icon: faGraduationCap,  color: "#564ab1" },
};
const TONE_FALLBACK = { icon: faMicrophone, color: "#9299a8" };

const GOAL_RECOMMENDATIONS = {
  reach:      { contentTypes: ["Educational", "Story"],              tones: ["Entertaining", "Informative"],   formats: ["ShortVideo", "Image", "Carousel"] },
  engagement: { contentTypes: ["Opinion", "Interactive"],            tones: ["Entertaining", "Persuasive"],    formats: ["Poll", "Text", "Image"] },
  brand:      { contentTypes: ["Educational", "Story", "Opinion"],   tones: ["Informative", "Instructional"],  formats: ["Carousel", "Text", "Image"] },
  retention:  { contentTypes: ["Story", "Educational"],              tones: ["Informative", "Entertaining"],   formats: ["Video", "Carousel", "Text"] },
  conversion: { contentTypes: ["Promotional", "Educational"],        tones: ["Persuasive", "Instructional"],   formats: ["Video", "Image", "Text"] },
};

const RecommendedBadge = () => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    background: "linear-gradient(135deg, #51d28c, #2ecc71)",
    color: "#fff", fontSize: 9, fontWeight: 700,
    borderRadius: 20, padding: "2px 7px", letterSpacing: 0.4,
    textTransform: "uppercase",
  }}>
    <FontAwesomeIcon icon={faStar} style={{ fontSize: 8 }} />
    Recommended
  </span>
);

const SelectionHistory = ({ slideIndex, formik, selectedPillar, setSlideIndex }) => {
  if (slideIndex === 0) return null;

  const getStepSummary = (stepIndex) => {
    switch (stepIndex) {
      case 0: {
        const p = PLATFORMS.find((x) => x.key === formik.values.platform);
        return p ? { icon: p.icon, color: p.color, label: p.label } : null;
      }
      case 1: {
        const g = GOALS.find((x) => x.key === formik.values.goal);
        return g ? { icon: g.icon, color: g.color, label: g.label } : null;
      }
      case 2:
        return selectedPillar ? { icon: faLayerGroup, color: "#564ab1", label: selectedPillar.pillar } : null;
      case 3: {
        const ct = CONTENT_TYPES.find((x) => x.key === formik.values.content_type);
        return ct ? { icon: ct.icon, color: ct.color, label: ct.label } : null;
      }
      case 4:
        return formik.values.tone ? { color: "#038edc", label: formik.values.tone } : null;
      case 5: {
        const fmt = FORMATS.find((x) => x.key === formik.values.post_type);
        return fmt ? { icon: fmt.icon, color: fmt.color, label: fmt.label } : null;
      }
      default:
        return null;
    }
  };

  const items = Array.from({ length: slideIndex }, (_, i) => {
    const summary = getStepSummary(i);
    return summary ? { stepIndex: i, ...summary } : null;
  }).filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
      {items.map(({ stepIndex, icon, color, label }) => (
        <button
          key={stepIndex}
          type="button"
          onClick={() => setSlideIndex(stepIndex)}
          title={`Edit ${STEPS[stepIndex].label}`}
          style={{
            display: "flex", alignItems: "center", gap: 5,
            padding: "4px 10px", borderRadius: 20,
            background: `${color}12`, border: `1px solid ${color}35`,
            cursor: "pointer", transition: "background 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = `${color}22`)}
          onMouseLeave={(e) => (e.currentTarget.style.background = `${color}12`)}
        >
          <span style={{ color: "#9299a8", fontWeight: 500, fontSize: 10 }}>
            {STEPS[stepIndex].label}:
          </span>
          {icon && (icon === faYoutube
            ? <YoutubeIcon height={20} style={{ marginLeft: 2 }} />
            : <FontAwesomeIcon icon={icon} style={{ fontSize: 10, color }} />)}
          <span style={{ fontSize: 11, fontWeight: 700, color }}>{label}</span>
        </button>
      ))}
    </div>
  );
};

const StepBar = ({ current }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 28 }}>
    {STEPS.map((_, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <div key={i} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : "none" }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: done ? "#51d28c" : active ? "#038edc" : "#eaecf0",
            color: done || active ? "#fff" : "#9299a8",
            fontSize: 11, fontWeight: 700, transition: "background 0.2s",
          }}>
            {done ? <FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} /> : i + 1}
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ flex: 1, height: 2, background: done ? "#51d28c" : "#eaecf0", transition: "background 0.2s" }} />
          )}
        </div>
      );
    })}
  </div>
);

const SectionLabel = ({ children }) => (
  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: "#9299a8", marginBottom: 16 }}>
    {children}
  </p>
);

const GoalStep = ({ formik, setSlideIndex, t }) => {
  const [showMix, setShowMix] = useState(false);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <SectionLabel>{t("Backend.Post.Assistant.ChooseGoal")}</SectionLabel>
        <button
          type="button"
          onClick={() => setShowMix((v) => !v)}
          style={{
            fontSize: 11, fontWeight: 600, color: "#564ab1",
            background: "none", border: "none", cursor: "pointer",
            padding: "0 0 16px", textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          {showMix ? "Hide tips ↑" : "What should I pick? ↓"}
        </button>
      </div>

      {/* Recommended mix panel */}
      {showMix && (
        <div style={{
          background: "rgba(86,74,177,0.04)", border: "1px solid rgba(86,74,177,0.15)",
          borderRadius: 12, padding: "14px 18px", marginBottom: 20,
        }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: "#564ab1", margin: "0 0 10px", textTransform: "uppercase", letterSpacing: 0.8 }}>
            Recommended content mix
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {GOALS.map(({ key, color, label, recommended }) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color, width: 64, flexShrink: 0 }}>{label}</span>
                <div style={{ flex: 1, height: 6, borderRadius: 10, background: "#eaecf0", overflow: "hidden" }}>
                  <div style={{ width: `${recommended}%`, height: "100%", background: color, borderRadius: 10, transition: "width 0.4s ease" }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#374151", width: 30, textAlign: "right" }}>{recommended}%</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 11, color: "#9299a8", margin: "10px 0 0", lineHeight: 1.5 }}>
            This is a general guide — not a rule. Adjust to your audience.
          </p>
        </div>
      )}

      {/* Goal cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {GOALS.map(({ key, icon, color, tag, label, description, example }) => (
          <button
            key={key}
            type="button"
            onClick={() => { formik.setFieldValue("goal", key); setSlideIndex(2); }}
            style={{
              flex: "1 1 160px", padding: "18px 16px", borderRadius: 14,
              border: "2px solid #e2e5ea", background: "#fff",
              cursor: "pointer", textAlign: "left", transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 4px 16px ${color}22`; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e5ea"; e.currentTarget.style.boxShadow = "none"; }}
          >
            {/* Icon + tag row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: `${color}18`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <FontAwesomeIcon icon={icon} style={{ color, fontSize: 18 }} />
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, color,
                background: `${color}15`, borderRadius: 20,
                padding: "2px 9px", letterSpacing: 0.5,
              }}>
                {tag}
              </span>
            </div>

            {/* Title */}
            <div style={{ fontWeight: 800, fontSize: 14, color: "#111827", marginBottom: 5 }}>{label}</div>

            {/* Description */}
            <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.55, marginBottom: 7 }}>{description}</div>

            {/* Example */}
            <div style={{ fontSize: 11, color: "#9299a8", fontStyle: "italic", lineHeight: 1.4 }}>
              {example}
            </div>
          </button>
        ))}
      </div>
    </>
  );
};

const PostAssistant = ({
  show,
  setShow,
  setPosts,
  setEditPost,
  posts,
  setShowEdit,
  showSpinner,
  setShowSpinner,
  createPostFn = null, // optional override: async (title, text) => { ok, data }
  pillarsEndpoint = null,
}) => {
  const { t } = useTranslation();
  const [slideIndex, setSlideIndex] = useState(0);
  const [aiPost, setAiPost] = useState(null);
  const [pillars, setPillars] = useState([]);
  const [selectedPillar, setSelectedPillar] = useState(null);

  const handleAIPost = (response) => setAiPost(response.data);

  const postAssistantForm = PostAssistantForm.createPostAssistantForm(null, handleAIPost, setShowSpinner);
  const formik = useFormik(postAssistantForm);
  const tones = t("Backend.Post.Tones", { returnObjects: true });

  useEffect(() => {
    if (show) {
      const endpoint = pillarsEndpoint ?? SocialEndpoints.posts.contentPillars.index;
      crudApi.getAll(endpoint, setPillars, "contentpillars");
    }
  }, [show]);

  const NavButtons = () => (
    <div style={{ display: "flex", gap: 8 }}>
      {slideIndex > 0 && slideIndex < 7 && (
        <button
          type="button"
          onClick={() => setSlideIndex(slideIndex - 1)}
          style={{
            width: 36, height: 36, borderRadius: 50, border: "1.5px solid #e2e5ea",
            background: "#fff", color: "#374151", fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 13 }} />
        </button>
      )}
      {slideIndex === 2 && (
        <button
          type="button"
          onClick={() => setSlideIndex(3)}
          style={{
            padding: "8px 20px", borderRadius: 50, border: "none",
            background: "linear-gradient(135deg,#038edc,#0270b8)",
            color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
            boxShadow: "0 4px 14px rgba(3,142,220,0.3)",
          }}
        >
          {t("Backend.Buttons.Next")} →
        </button>
      )}
      {slideIndex === 6 && (
        <button
          type="button"
          onClick={() => formik.handleSubmit()}
          style={{
            padding: "8px 20px", borderRadius: 50, border: "none",
            background: "linear-gradient(135deg,#564ab1,#3d3490)",
            color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
            boxShadow: "0 4px 14px rgba(86,74,177,0.35)",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          {t("Backend.Post.Assistant.CreatePost")}
        </button>
      )}
      {aiPost && (
        <button
          type="button"
          onClick={async () => {
            let response;
            const contentPillar = formik.values.content_pillar;
            if (createPostFn) {
              response = await createPostFn(formik.values.title, aiPost, contentPillar);
            } else {
              response = await crudApi.createNew(SocialEndpoints.posts.new, {
                title: formik.values.title,
                text: aiPost,
                content_pillar: contentPillar,
              });
            }
            if (response?.ok) {
              listHandler.newToList(response, posts, setPosts, null, true);
              setEditPost({ ...response.data, content_pillar: contentPillar });
              setShowEdit(true);
            }
          }}
          style={{
            padding: "8px 20px", borderRadius: 50, border: "none",
            background: "linear-gradient(135deg,#51d28c,#2da866)",
            color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
            boxShadow: "0 4px 14px rgba(81,210,140,0.35)",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <FontAwesomeIcon icon={faSave} />
          {t("Backend.Post.Assistant.Save")}
        </button>
      )}
    </div>
  );

  return (
    <NewModal
      show={show}
      setShow={setShow}
      onExit={() => {
        setSlideIndex(0);
        setAiPost(null);
        setSelectedPillar(null);
        setPillars([]);
        formik.resetForm();
      }}
      title={t("Backend.Post.Assistant.Title")}
      ExtraButton={NavButtons}
      size="xl"
    >
      <StepBar current={slideIndex} />
      <SelectionHistory
        slideIndex={slideIndex}
        formik={formik}
        selectedPillar={selectedPillar}
        setSlideIndex={setSlideIndex}
      />

      {/* Step 0 — Platform */}
      {slideIndex === 0 && (
        <>
          <SectionLabel>{t("Backend.Post.Assistant.ChoosePlatform")}</SectionLabel>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {PLATFORMS.map(({ key, icon, color, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => { formik.setFieldValue("platform", key); setSlideIndex(1); }}
                style={{
                  flex: "0 0 140px", padding: "24px 20px", borderRadius: 14,
                  border: "2px solid #e2e5ea", background: "#fff",
                  cursor: "pointer", textAlign: "center", transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 4px 16px ${color}22`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e5ea"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{
                  width: 48, height: 48, borderRadius: 12, margin: "0 auto 12px",
                  background: `${color}18`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {key === "youtube"
                    ? <YoutubeIcon height={24} />
                    : <FontAwesomeIcon icon={icon} style={{ color, fontSize: 24 }} />}
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{label}</div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Step 1 — Goal */}
      {slideIndex === 1 && (
        <GoalStep formik={formik} setSlideIndex={setSlideIndex} t={t} />
      )}

      {/* Step 2 — Content Pillar */}
      {slideIndex === 2 && (
        <>
          <SectionLabel>
            <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: 6 }} />
            {t("Backend.Post.Assistant.ChoosePillar")}
          </SectionLabel>
          {pillars.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {pillars.map((pillar, index) => {
                const color = PILLAR_COLORS[index % PILLAR_COLORS.length];
                const selected = selectedPillar?.id === pillar.id;
                return (
                  <button
                    key={pillar.id}
                    type="button"
                    onClick={() => {
                      setSelectedPillar(pillar);
                      formik.setFieldValue("content_pillar", { id: pillar.id });
                      setSlideIndex(3);
                    }}
                    style={{
                      flex: "1 1 180px", padding: "14px 18px", borderRadius: 12, textAlign: "left",
                      border: `2px solid ${selected ? color : "#e2e5ea"}`,
                      background: selected ? `${color}10` : "#fff",
                      cursor: "pointer", transition: "all 0.15s ease",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{pillar.pillar}</span>
                      {selected && <FontAwesomeIcon icon={faCheck} style={{ color, fontSize: 11, marginLeft: "auto" }} />}
                    </div>
                    {pillar.description && (
                      <p style={{ fontSize: 12, color: "#6b7280", margin: "6px 0 0", lineHeight: 1.5 }}>{pillar.description}</p>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 0", color: "#9299a8" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px dotted #038edc", animation: "dotted-spin 0.9s linear infinite" }} />
              {t("Backend.Post.Assistant.LoadingPillars")}
            </div>
          )}
        </>
      )}

      {/* Step 3 — Content Type */}
      {slideIndex === 3 && (
        <>
          <SectionLabel>{t("Backend.Post.Assistant.ChooseContentType")}</SectionLabel>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {CONTENT_TYPES.map(({ key, icon, color, label, description }) => {
              const isRec = GOAL_RECOMMENDATIONS[formik.values.goal]?.contentTypes.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { formik.setFieldValue("content_type", key); setSlideIndex(4); }}
                  style={{
                    flex: "1 1 160px", padding: "20px 16px", borderRadius: 14,
                    border: `2px solid ${isRec ? "#51d28c" : "#e2e5ea"}`,
                    background: isRec ? "rgba(81,210,140,0.04)" : "#fff",
                    cursor: "pointer", textAlign: "left", transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 4px 16px ${color}22`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = isRec ? "#51d28c" : "#e2e5ea"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: `${color}18`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <FontAwesomeIcon icon={icon} style={{ color, fontSize: 18 }} />
                    </div>
                    {isRec && <RecommendedBadge />}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>{description}</div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Step 4 — Tone */}
      {slideIndex === 4 && (
        <>
          <SectionLabel>{t("Backend.Post.Assistant.ChooseTone")}</SectionLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {Object.keys(tones).map((toneKey) => {
              const isRec = GOAL_RECOMMENDATIONS[formik.values.goal]?.tones.includes(toneKey);
              const { icon, color } = TONE_META[toneKey] ?? TONE_FALLBACK;
              return (
                <button
                  key={toneKey}
                  type="button"
                  onClick={() => { formik.setFieldValue("tone", toneKey); setSlideIndex(5); }}
                  style={{
                    flex: "1 1 160px", padding: "20px 16px", borderRadius: 14,
                    border: `2px solid ${isRec ? "#51d28c" : "#e2e5ea"}`,
                    background: isRec ? "rgba(81,210,140,0.04)" : "#fff",
                    cursor: "pointer", textAlign: "left",
                    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 4px 16px ${color}22`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = isRec ? "#51d28c" : "#e2e5ea"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: `${color}18`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <FontAwesomeIcon icon={icon} style={{ color, fontSize: 18 }} />
                    </div>
                    {isRec && <RecommendedBadge />}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 5 }}>{toneKey}</div>
                  <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>{tones[toneKey].example}</div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Step 5 — Format */}
      {slideIndex === 5 && (
        <>
          <SectionLabel>{t("Backend.Post.Assistant.ChoosePostType")}</SectionLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {FORMATS.map(({ key, icon, color, label }) => {
              const isRec = GOAL_RECOMMENDATIONS[formik.values.goal]?.formats.includes(key);
              const isSelected = formik.values.post_type === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { formik.setFieldValue("post_type", key); setSlideIndex(6); }}
                  style={{
                    flex: "1 1 130px", padding: "20px 16px", borderRadius: 14,
                    border: `2px solid ${isSelected ? color : isRec ? "#51d28c" : "#e2e5ea"}`,
                    background: isSelected ? `${color}18` : isRec ? "rgba(81,210,140,0.04)" : "#fff",
                    cursor: "pointer", textAlign: "center",
                    transition: "border-color 0.15s, background 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 4px 16px ${color}22`; }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = isSelected ? color : isRec ? "#51d28c" : "#e2e5ea";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div style={{ color, fontSize: 22, marginBottom: 8 }}>
                    <FontAwesomeIcon icon={icon} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#111827", marginBottom: isRec ? 6 : 0 }}>{label}</div>
                  {isRec && <div style={{ marginTop: 6 }}><RecommendedBadge /></div>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Step 6 — Topic / Input + Generate + AI result */}
      {slideIndex === 6 && (
        <>
          <SectionLabel>{t("Backend.Post.Assistant.ChooseTopicInput") || "WHAT SHOULD THE POST BE ABOUT?"}</SectionLabel>
          <TextArea
            formik={formik}
            valueName="text"
            label={t("Backend.Post.Assistant.TopicPlaceholder") || "What should the post be about?"}
            rows={5}
            placeholder="Describe your idea, key message, story, tip, offer, or context."
          />

          {showSpinner && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "24px 0", color: "#9299a8" }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", border: "2px dotted #564ab1", animation: "dotted-spin 0.9s linear infinite" }} />
              {t("Backend.Post.AIworking")}
            </div>
          )}
          {aiPost && !showSpinner && (
            <div style={{ marginTop: 20 }}>
              <SectionLabel>{t("Backend.Post.Assistant.AIPost")}</SectionLabel>
              <div style={{
                padding: "20px 24px", borderRadius: 12,
                background: "linear-gradient(135deg,rgba(86,74,177,0.05) 0%,rgba(86,74,177,0.02) 100%)",
                border: "1px solid rgba(86,74,177,0.15)",
                fontSize: 14, lineHeight: 1.8, color: "#374151",
                whiteSpace: "pre-wrap",
              }}
              >
                {aiPost}
              </div>
            </div>
          )}
        </>
      )}
    </NewModal>
  );
};

export default PostAssistant;
